import { DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { PhotoLOD } from '../photo-lod.js';
import { ImageSize } from '../image-size.js';
import { kGenericBlurHash } from '../../../utility/constants.js';
import { FileReferenceBase } from './file-reference-base.js';

export class PhotoReference extends FileReferenceBase {
  static readonly TYPE_ID: string = 'f189261d8ef94b208771d4dbc833dece';
  static readonly SCHEMA_VERSION: number = 4;

  public readonly hasImageSize: boolean;
  public readonly aspectRatio: number;

  private constructor(
    public readonly uniqueId: string,
    public readonly storagePath: string,
    public readonly downloadUrl: string,
    public readonly width: number,
    public readonly height: number,
    public readonly lods: PhotoLOD[],
  ) {
    super();
    this.hasImageSize = width > 0 && height > 0;
    this.aspectRatio = this.hasImageSize ? width / height : 0.0;
  }

  get $typeId() {
    return PhotoReference.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(PhotoReference.SCHEMA_VERSION);
  }

  static create(opts: {
    uniqueId: string,
    storagePath: string,
    downloadUrl: string,
    width: number,
    height: number,
    lods?: PhotoLOD[],
  }) {
    return new PhotoReference(
        opts.uniqueId,
        opts.storagePath,
        opts.downloadUrl,
        opts.width,
        opts.height,
        opts.lods ?? []
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PhotoReference {
    return new PhotoReference(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'storagePath'),
        MapUtils.get<string>(map, 'downloadUrl'),
        MapUtils.getNumber(map, 'width'),
        MapUtils.getNumber(map, 'height'),
        MapUtils.getList<PhotoLOD>(
            map,
            'lods',
            (value: unknown) => context.fromData<PhotoLOD>(value as SerializedData),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'storagePath': this.storagePath,
      'downloadUrl': this.downloadUrl,
      'width': this.width,
      'height': this.height,
      'lods': this.lods.map((p) => context.toData(p)),
    };
  }

  clone(): this {
    return new PhotoReference(
        this.uniqueId,
        this.storagePath,
        this.downloadUrl,
        this.width,
        this.height,
        this.lods.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: PhotoReference): boolean {
    if(other === this) {
      return true;
    }

    return other.height === this.height &&
        other.width === this.width &&
        other.uniqueId === this.uniqueId &&
        other.storagePath === this.storagePath &&
        other.downloadUrl === this.downloadUrl &&
        isSameList(other.lods, this.lods, isSameData);
  }

  copyWith(data: Partial<PhotoReference>): this {
    return new PhotoReference(
        data.uniqueId ?? this.uniqueId,
        data.storagePath ?? this.storagePath,
        data.downloadUrl ?? this.downloadUrl,
        data.width ?? this.width,
        data.height ?? this.height,
        data.lods ?? this.lods.map((e) => e.clone()),
    ) as this;
  }
}

export class PhotoReferenceBuilder extends DataModelBuilder<PhotoReference> {
  build(context: SerializationContext, map: SerializedData): PhotoReference {
    return PhotoReference.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(PhotoReference.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PhotoReference.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      version = 2;
    }

    if(version < 3) {
      version = 3;
      map['lods'] = [];
    }

    if(version < 4) {
      version = 4;

      const imageSize = context.fromDataOrNull<ImageSize>(MapUtils.getOrNull<SerializedData>(map, 'imageSize'));
      map['width'] = imageSize?.width ?? 0;
      map['height'] = imageSize?.height ?? 0;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: PhotoReference,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to ?? PhotoReference.SCHEMA_VERSION;

    if(version >= 4) {
      version = 1;

      map['imageSize'] = null;
      map['blurHash'] = kGenericBlurHash;
      map['$schema_version'] = VersionRange.encodedBetween(1, 4);
    }

    return map;
  }
}
